
import { useState, useRef, ChangeEvent, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { UserProfile } from "@/types";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import BackgroundCheckComponent from "@/components/admin/BackgroundCheckComponent";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Upload, X, UserCircle } from "lucide-react";

const ProfilePage = () => {
  const { profile, user, updateProfile } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState<Partial<UserProfile>>({
    name: profile?.name || "",
    phone: profile?.phone || "",
    address: profile?.address || "",
  });

  // Update form data when profile changes - fixed: changed useState to useEffect
  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || "",
        phone: profile.phone || "",
        address: profile.address || "",
      });
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if user exists
    if (!user || !user.id) {
      toast({
        title: "Error",
        description: "User information is missing. Please try logging in again.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update({
          name: formData.name,
          phone: formData.phone,
          address: formData.address,
        })
        .eq('id', user.id)
        .select()
        .maybeSingle();

      if (error) throw error;

      // If we have an updateProfile function in AuthContext, call it
      if (typeof updateProfile === 'function' && data) {
        updateProfile(data);
      }

      toast({
        title: "Success",
        description: "Your profile has been updated.",
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    }
  };

  const uploadProfileImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) {
      // Show toast if user is not found
      if (!user?.id) {
        toast({
          title: "Error",
          description: "User information is missing. Please try logging in again.",
          variant: "destructive",
        });
      }
      return;
    }

    try {
      setIsUploading(true);
      
      // Create a unique file path for the user's profile image
      const filePath = `profile-images/${user.id}/${Date.now()}-${file.name}`;
      
      // Upload the file to Supabase Storage
      const { data, error } = await supabase.storage
        .from('profile-images')
        .upload(filePath, file, {
          upsert: true,
        });
      
      if (error) throw error;

      // Get the public URL for the uploaded file
      const { data: { publicUrl } } = supabase.storage
        .from('profile-images')
        .getPublicUrl(data.path);

      // Update the user profile with the image URL
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ profileimage: publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // If we have an updateProfile function in AuthContext, call it
      if (profile && typeof updateProfile === 'function') {
        updateProfile({
          ...profile,
          profileimage: publicUrl,
        } as UserProfile);
      }

      toast({
        title: "Success",
        description: "Profile photo uploaded successfully.",
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      toast({
        title: "Error",
        description: "Failed to upload profile photo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      // Reset the input field
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeProfileImage = async () => {
    if (!user?.id || !profile?.profileimage) return;
    
    try {
      setIsUploading(true);
      
      // Extract file path from the URL
      const url = new URL(profile.profileimage);
      const pathParts = url.pathname.split('/');
      const bucketIndex = pathParts.indexOf('profile-images');
      const filePath = pathParts.slice(bucketIndex).join('/');
      
      // Delete the file from storage
      const { error } = await supabase.storage
        .from('profile-images')
        .remove([filePath]);
      
      if (error) throw error;
      
      // Update the profile to remove the image URL
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ profileimage: null })
        .eq('id', user.id);
      
      if (updateError) throw updateError;
      
      // If we have an updateProfile function in AuthContext, call it
      if (typeof updateProfile === 'function') {
        updateProfile({
          ...profile,
          profileimage: undefined,
        } as UserProfile);
      }
      
      toast({
        title: "Success",
        description: "Profile photo removed successfully.",
      });
    } catch (error) {
      console.error("Error removing profile image:", error);
      toast({
        title: "Error",
        description: "Failed to remove profile photo. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Generate initials for avatar fallback
  const getInitials = (name: string = '') => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="container py-8 animate-fade-in">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-6 text-gradient">Profile Settings</h1>
        
        <Tabs defaultValue="personal">
          <TabsList className="mb-6">
            <TabsTrigger value="personal">Personal Information</TabsTrigger>
            <TabsTrigger value="verification">Verification</TabsTrigger>
          </TabsList>
          
          <TabsContent value="personal">
            <Card className="glass mb-6">
              <CardHeader>
                <CardTitle className="text-xl">Profile Photo</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center sm:items-start gap-4">
                <div className="flex items-center gap-6">
                  <div className="relative h-24 w-24 rounded-full overflow-hidden border-2 border-primary">
                    <Avatar className="h-full w-full">
                      {profile?.profileimage ? (
                        <AvatarImage src={profile.profileimage} alt={profile.name || "User"} />
                      ) : (
                        <AvatarFallback className="bg-primary/20 text-xl">
                          {profile?.name ? getInitials(profile.name) : <UserCircle className="h-12 w-12" />}
                        </AvatarFallback>
                      )}
                    </Avatar>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="flex gap-2"
                      onClick={triggerFileInput}
                      disabled={isUploading}
                    >
                      <Upload className="h-4 w-4" />
                      Upload Photo
                    </Button>
                    
                    {profile?.profileimage && (
                      <Button 
                        type="button" 
                        variant="destructive" 
                        className="flex gap-2"
                        onClick={removeProfileImage}
                        disabled={isUploading}
                      >
                        <X className="h-4 w-4" />
                        Remove
                      </Button>
                    )}
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={uploadProfileImage}
                      accept="image/*"
                      className="hidden"
                      disabled={isUploading}
                    />
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  Upload a profile picture to personalize your account. JPG, GIF or PNG. Max 2MB.
                </p>
              </CardContent>
            </Card>
            
            <Card className="glass">
              <CardHeader>
                <CardTitle className="text-xl">Personal Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="text-sm font-medium block mb-1.5">
                      Name
                    </label>
                    <Input
                      id="name"
                      value={formData.name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Your name"
                      className="glass bg-white/5"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="text-sm font-medium block mb-1.5">
                      Phone
                    </label>
                    <Input
                      id="phone"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Your phone number"
                      className="glass bg-white/5"
                    />
                  </div>

                  <div>
                    <label htmlFor="address" className="text-sm font-medium block mb-1.5">
                      Address
                    </label>
                    <Input
                      id="address"
                      value={formData.address || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Your address"
                      className="glass bg-white/5"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={isUploading}
                  >
                    Save Changes
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="verification">
            <BackgroundCheckComponent 
              userId={user?.id} 
              userType="customer" 
              userName={profile?.name}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfilePage;
